// src/hooks/useInitializeProject.js
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setProjectDetails,
  setError,
  setProjectInitialized,
  setUserDetails,
} from "../../../store/slices/projectSetup";
import {
  setCanvasSize,
  setEditorType,
  setMinPages,
  setMaxPages,
  setCalendarSettings,
  setSettings,
} from "../../../store/slices/canvas";

import { apiGet, apiPost } from "../common-services/apiCall";
import { ENDPOINTS } from "../constants/apiurl";
import { processProjectPages } from "../services/theme/index.js";
import { resolveProjectSize } from "../helpers/projectSize.js";
import { EDITOR_TYPES, USER_TYPES } from "../constants/index.js";

import { setBrandDetails } from "../../../store/slices/brandDetails.js";
import { getActiveEditorType } from "../helpers/canvasSliceGetters.js";
import { Encrypt } from "../common-functions";
import { setEditorConfigurations } from "../../../store/slices/editorConfigurations";

/**
 * Custom hook to initialize project details based on URL parameters.
 */
const useInitializeProject = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const domain = window.location.hostname;
  // const domain = "editor.printpoz.com";
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setErrorState] = useState(null);
  const { brandname } = useSelector((state) => state.brandDetails);
  const projectState = useSelector((state) => state.projectSetup);
  let editorType = "";
  // create function to get project details
  const getProjectDetails = async (project_id, token) => {
    const response = await apiGet(ENDPOINTS.getProjectDetails + project_id);

    if (response && response.items) {
      const { editorDetails, cartDetails } = response.items;

      // Dispatch project details to Redux store
      let brand_id = "";
      // Size the saved pages are scaled to further down. Stays null on the
      // no-saved-design path, where the pages come from a theme instead.
      let targetSize = null;
      if (
        editorDetails &&
        editorDetails !== null &&
        editorDetails !== undefined
      ) {
        brand_id = editorDetails.brand_id;
        editorType = editorDetails.editor_type;
        // setActiveEditorType(editorType);
        dispatch(setEditorType(editorType));

        // Cart size wins over the size the design was saved at (see
        // helpers/projectSize.js). `!token` is this file's existing test for
        // "customer" (see the localStorage write further down).
        targetSize = resolveProjectSize({
          editorDetails,
          cartDetails,
          editorType,
          isCustomer: !token,
        });
        dispatch(setCanvasSize(targetSize));
      } else if (
        cartDetails &&
        cartDetails !== null &&
        cartDetails !== undefined
      ) {
        const editorType = cartDetails.editor_type;
        brand_id = cartDetails.brand_id;
        // setActiveEditorType(editorType);
        dispatch(setEditorType(editorType));
        if (editorType === EDITOR_TYPES.PHOTOBOOK || editorType === EDITOR_TYPES.LAYFLATALBUM) {
          dispatch(
            setCanvasSize({
              width: cartDetails.size_id.width * 2,
              height: cartDetails.size_id.height,
              depth: 0,
              safeMargin: cartDetails.size_id?.safe_margin || 0,
              bleedMargin: cartDetails.size_id?.bleed_margin || 0,
            })
          ); // width is doubled for photobook
        } else {
          if (cartDetails.size_id) {
            dispatch(
              setCanvasSize({
                width: cartDetails.size_id.width,
                height: cartDetails.size_id.height,
                depth: cartDetails.size_id?.depth || 0,
                safeMargin: cartDetails.size_id?.safe_margin || 0,
                bleedMargin: cartDetails.size_id?.bleed_margin || 0,
              })
            );
          }
        }
      }
      if (editorType === EDITOR_TYPES.CALENDER) {
        if (editorDetails.cal_settings) {
          let calSettings = { ...editorDetails.cal_settings };
          if (editorDetails.startMonth) {
            calSettings.startMonth = null;
          }
          if (editorDetails.startYear) {
            calSettings.startYear = null;
          }
          // If weeksColumns missing from saved cal_settings, read from first calendar object
          if (calSettings.weeksColumns == null) {
            const pages = Array.isArray(editorDetails.pages_c) ? editorDetails.pages_c : [];
            outer: for (const page of pages) {
              for (const layout of (page.layout || [])) {
                for (const obj of (layout?.objects || [])) {
                  if ((obj.type === 'calendar' || obj.type === 'multiple-calendar') && obj.calendarSettings?.weeksColumns != null) {
                    calSettings.weeksColumns = obj.calendarSettings.weeksColumns;
                    break outer;
                  }
                }
              }
            }
          }
          dispatch(setCalendarSettings(calSettings));

          const settings =
            editorDetails &&
              editorDetails !== null &&
              editorDetails !== undefined &&
              editorDetails.settings &&
              editorDetails.calSettings !== null &&
              editorDetails.calSettings !== undefined
              ? editorDetails.settings
              : { subtype: "wall_calendar" };
          dispatch(setSettings(settings));
        } else {
          // lets get current month and year
          const date = new Date();
          const currentYear = date.getFullYear();
          const currentMonth = date.getMonth() + 1;
          dispatch(
            setCalendarSettings({
              startMonth: currentMonth,
              startYear: currentYear,
              addCover: false,
            })
          );
        }
      }

      if (
        cartDetails &&
        cartDetails != null &&
        cartDetails != undefined &&
        cartDetails.min_pages &&
        cartDetails.max_pages
      ) {
        dispatch(setMinPages(cartDetails.min_pages));
        dispatch(setMaxPages(cartDetails.max_pages));
      }

      if (
        editorDetails &&
        editorDetails != null &&
        editorDetails != undefined &&
        editorDetails.pages_c
      ) {
        // targetSize, not editorDetails.*: this is what rescales the design
        // when the cart size changed after the last save.
        const size = targetSize || editorDetails;
        let width = size.width;
        if (editorDetails.editor_type === EDITOR_TYPES.PHOTOBOOK) {
          width = size.width / 2;
        }
        const pages = processProjectPages(
          editorDetails.pages_c,
          width,
          size.height
        );
        editorDetails.pages_c = pages;
      }
      // lets clear user
      if (!token) {
        localStorage.removeItem("userDetails");
        // lets set user details in localhost for future use
        const user = {
          userTypeCode: USER_TYPES.CUSTOMER,
          brand_id: brand_id,
          _id: cartDetails.userId || cartDetails?.user_id,
        };
        localStorage.setItem("userDetails", JSON.stringify(user));
        dispatch(setUserDetails(user));
      }
      if (editorDetails && editorDetails.settings) {
        dispatch(setSettings(editorDetails.settings));
      } else if (cartDetails && cartDetails.settings) {
        dispatch(setSettings(cartDetails.settings));
      }

      dispatch(setProjectDetails({ editorDetails, cartDetails }));

      // NOTE: Smart Text hydration for the saved-project path is handled in
      // useThemeSetup.setupTheme(), which runs after setProjectDetails and reads
      // themeDetails.smart_text (= editorDetails.smart_text). No need to dispatch
      // setTextGroups here — setupTheme will do it with objectIds intact so the
      // reducer can properly re-link objects before propagating group values.
    } else {
      console.error("Error fetching  project details:", response);
      dispatch(setError("Failed to load project")); // Dispatch error to Redux
      setErrorState("Failed"); // Set local error state
    }

    setLoading(false);
  };
  const getUserDetails = async (token) => {
    let data = {
      authorization: "Bearer " + token,
    };

    const response = await apiPost(ENDPOINTS.fetchUserDataFromToken, data);

    if (response && response.status === 0) {
      throw new Error(response.message || "Invalid Token");
    }

    if (response && response.items && !Array.isArray(response.items)) {
      let user = response.items;
      if (user && user._id) {
        user.token = token;
        localStorage.setItem("userDetails", JSON.stringify(user));
        dispatch(setUserDetails(user));
      } else {
        localStorage.removeItem("userDetails");
        throw new Error("Failed to load user details");
      }
    } else {
      console.error("Error fetching  user details:", response);
      throw new Error(response?.message || "Failed to load user");
    }
  };

  const getBrandDetails = async ({ project_id = null }) => {
    try {
      let dataObj = {
        editor_domain: `https://${domain}`,
        // editor_domain: `https://editor.printpoz.com`,

      }
      if (project_id) {
        dataObj["project_id"] = project_id
      }
      const response = await apiPost(ENDPOINTS.getBrandDetails, dataObj);
      if (response.status === 1) {
        dispatch(
          setBrandDetails({
            brandname: response.items.brandname,
            brand_logo: response.items.brand_logo,
            editor_url: response.items.editor_url,
            redirect_url: response.items.redirect_url,
            editor_favicon_icon: response.items.editor_favicon_icon,
            _id: response.items.brand_id,
          })
        );
        // Always persist top-level store_id and brand_id (API may return editorConfigurations: null)
        const topLevelConfig = {};
        if (response?.items?.store_id) topLevelConfig.store_id = response.items.store_id;
        if (response?.items?.brand_id) topLevelConfig.brand_id = response.items.brand_id;
        if (Object.keys(topLevelConfig).length > 0) {
          dispatch(setEditorConfigurations(topLevelConfig));
        }
        if (response?.items?.editorConfigurations) {
          dispatch(setEditorConfigurations(response.items.editorConfigurations))
        }

        // store encrypted brand details in local storage
        localStorage.setItem(
          "brandDetails",
          Encrypt({
            brandname: response.items.brandname,
            brand_id: response.items.brand_id,
          })
        );

        const title =
          response.items && response.items.brandname
            ? response.items.brandname
            : `${editorType}-Designer`;

        document.title = title;

        // Dynamically set the document faviconicon
        // Check if a favicon already exists
        let faviconIcon = document.querySelector("link[rel='icon']");
        if (
          faviconIcon &&
          response.items &&
          response.items.editor_favicon_icon
        ) {
          // If it exists, update it; if not, create a new one
          faviconIcon.href = response.items.editor_favicon_icon;
        } else if (response.items && response.items.editor_favicon_icon) {
          faviconIcon = document.createElement("link");
          faviconIcon.rel = "icon";
          faviconIcon.href = response.items.editor_favicon_icon;
          document.head.appendChild(faviconIcon);
        }
      } else {
        const title = `${editorType}-Designer`;

        document.title = title;
      }
    } catch (error) { }
  };

  useEffect(() => {
    const initializeProject = async () => {
      try {
        // Get cart order id
        const urlParams = new URLSearchParams(location.search);
        let project_id = urlParams.get("c_id"); // get cart order id
        const token = urlParams.get("u_id"); // get token  as this used for admin to create
        const shareCode = urlParams.get("s"); // shared design opened by an edit link

        // A share link resolves to its cart order id HERE and the id is never written
        // back into the URL. Putting it there would hand the recipient the raw c_id,
        // and they would keep full access forever even after the owner resets the
        // share link. Everything downstream must read the id from redux
        // (`getCartOrderId`), not from the address bar.
        if (!project_id && shareCode) {
          const shareResponse = await apiPost(ENDPOINTS.viewByShareCode, {
            share_code: shareCode,
          });
          const shareItems = shareResponse?.items;
          if (shareItems?.mode === "edit" && shareItems.cart_order_id) {
            project_id = shareItems.cart_order_id;
          } else {
            // A view code (or a dead one) has no business in the editor. Send it to
            // the preview route, which is the only thing it can open.
            navigate(`/preview?s=${encodeURIComponent(shareCode)}`, { replace: true });
            return;
          }
        }

        // if project_id or token is missing, redirect to login
        if (!project_id && !token) {
          navigate("/login");
          return;
        }

        // setUp brand details
        if (project_id && !token) {
          await getBrandDetails({ project_id });
        }

        if (token) {
          await getUserDetails(token);
        }
        if (project_id) {
          await getProjectDetails(project_id, token);
        }

        dispatch(setProjectInitialized(true));

        setLoading(false); // Mark loading as false after successful fetch
      } catch (err) {
        console.error("Error initializing project:", err);
        dispatch(setError(err.message)); // Dispatch error to Redux
        setErrorState(err.message); // Set local error state
        setLoading(false); // Mark loading as false even if there's an error
      }
    };

    // Only initialize if the URL parameters are available
    initializeProject();
  }, [location.search]);

  // Return loading state, error, and current project state
  return { loading, error, ...projectState };
};

export default useInitializeProject;
